import { GlobalStyles } from '@spotify-internal/encore-creator-web';
import React from 'react';
import { connect } from 'react-redux';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { LandingPage } from '../LandingPage';
import { LOADING, UNAUTHORIZED, selectMeStatus, selectMe } from '../../store/selectors';
import { loadMe } from '../../store/me';
import * as Sentry from '@sentry/browser';
import { Helmet } from 'react-helmet';

import { ConnectedRoot } from '../../components/Root';
import { LocationTrackerWithRouter } from './LocationTracker';
import { Me, ReduxState, LocalePreferenceMap } from '../../types';
import { EmailUnsubscribe } from '../EmailUnsubscribe';
import { MeContext } from '../../context/me';
import { IntlProvider } from 'react-intl';
import es_MX from '../../translations/translations_es-MX.json';
import pt_BR from '../../translations/translations_pt-BR.json';
import { LOCALE_PREFERENCES, PODCASTERS_ZENDESK_LINK } from '../../lib/constants';
import { LocalePreferenceContext } from '../../context/localePreference';

interface Props {
  me?: Me;
  status?: 'LOADING' | 'ERROR' | 'UNAUTHORIZED';
  loadMe(): void;
  initialRoute?: string;
}
class App extends React.Component<Props> {
  componentDidMount() {
    this.props.loadMe();
  }

  // istanbul ignore next
  componentDidCatch(error: any, info: any) {
    Sentry.withScope(scope => {
      scope.setTag('page_locale', navigator.language);
      scope.setExtras(info);
      Sentry.captureException(error);
    });
  }

  render() {
    const { me, status, initialRoute } = this.props;
    if (status === LOADING) {
      return <span data-qa="app-loading" />;
    }

    const localePreference = getLocalePreference(window.navigator.language, LOCALE_PREFERENCES);
    const messages = {
      'pt-BR': pt_BR,
      'es-MX': es_MX,
    };

    // We can safely ignore this since Jest is doing some crazy mocking magic for our tests.
    // istanbul ignore next
    const initialEntries = initialRoute ? [initialRoute] : undefined;
    return (
      <>
        <GlobalStyles noFonts />
        <Helmet
          defaultTitle="Spotify for Podcasters"
          title={''}
          titleTemplate={'%s - Spotify for Podcasters'}
        />
        <MeContext.Provider value={me ? me : null}>
          <LocalePreferenceContext.Provider value={localePreference}>
            <IntlProvider
              locale={localePreference.s4pLocaleCode}
              messages={messages[localePreference.s4pLocaleCode]}
            >
              {
                // @ts-ignore
                <BrowserRouter initialEntries={initialEntries}>
                  <LocationTrackerWithRouter>
                    <Switch>
                      <Route path="/subscription/:subscriptionToken" component={EmailUnsubscribe} />
                      <Route path="/promocards">
                        {() => {
                          window.location.href = 'http://promocards.byspotify.com/podcasters';
                          return; // prevent the redirect URL being shown in the browser content window
                        }}
                      </Route>
                      <Route exact path="/" component={LandingPage} />

                      {status === UNAUTHORIZED && (
                        <Route exact path="/faq">
                          {() =>
                            (window.location.href =
                              PODCASTERS_ZENDESK_LINK + localePreference.languageAndRegionCode)
                          }
                        </Route>
                      )}
                      <Route path="*" component={ConnectedRoot} />
                    </Switch>
                  </LocationTrackerWithRouter>
                </BrowserRouter>
              }
            </IntlProvider>
          </LocalePreferenceContext.Provider>
        </MeContext.Provider>
      </>
    );
  }
}

function mapStateToProps(state: ReduxState) {
  return {
    status: selectMeStatus(state),
    me: selectMe(state),
  };
}

const actions = { loadMe };

export const ConnectedApp = connect(mapStateToProps, actions)(App);

export function getLocalePreference(langaugeCode: string, localePreferences: LocalePreferenceMap) {
  const supportedLangs = Object.keys(localePreferences);
  const userLang = langaugeCode;
  const isValidLang = supportedLangs.includes(userLang);
  const locale = isValidLang ? userLang : 'en';
  return LOCALE_PREFERENCES[locale];
}
